import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { devicesAPI } from '../services/api';
import { Plus, RefreshCw, Wifi, WifiOff, Grid3X3, X, Edit2 } from 'lucide-react';

export default function Devices() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ device_number: '', name: '', location: '' });
  const [syncing, setSyncing] = useState({});
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', location: '', address: '', support_phone: '' });
  const [editSaving, setEditSaving] = useState(false);
  
  const openEdit = (d) => {
    setEditing(d);
    setEditForm({
      name: d.name || '',
      location: d.location || '',
      address: d.address || '',
      support_phone: d.support_phone || ''
    });
  };
  
  const saveEdit = async () => {
    setEditSaving(true);
    try {
      await devicesAPI.update(editing.id, editForm);
      setEditing(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    }
    setEditSaving(false);
  };
  const navigate = useNavigate();

  const [lastCheck, setLastCheck] = useState(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await devicesAPI.list();
      setDevices(res.data);
    } catch (e) { console.error(e); }
    if (!silent) setLoading(false);
  };

  // One request checks every locker (a single call to the vendor cloud),
  // every 60 s, and not while this tab is in the background.
  const checkAllDevices = async () => {
    if (document.hidden) return;
    try {
      await devicesAPI.checkAllStatus();
      await load(true);
      setLastCheck(new Date());
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    load();
    checkAllDevices();
    const interval = setInterval(checkAllDevices, 60000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addDevice = async () => {
    if (!form.device_number) return;
    try {
      await devicesAPI.create(form);
      setShowAdd(false);
      setForm({ device_number: '', name: '', location: '' });
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const syncBoxes = async (id) => {
    setSyncing(p => ({ ...p, [id]: true }));
    try {
      const res = await devicesAPI.syncBoxes(id);
      alert(res.data.message);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Sync error'); }
    setSyncing(p => ({ ...p, [id]: false }));
  };

  const checkStatus = async (id) => {
    try {
      const res = await devicesAPI.checkStatus(id);
      alert(`Device ${res.data.device_number}: ${res.data.online ? 'ONLINE' : 'OFFLINE'}`);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div>
      <div style={s.header}>
        <h1 style={s.title}>Devices</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={s.outlineBtn} onClick={() => load(false)}><RefreshCw size={14} /> Refresh</button>
          <button style={s.primaryBtn} onClick={() => setShowAdd(true)}><Plus size={14} /> Add Device</button>
        </div>
      </div>

      <div style={s.panel}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Device Number</th>
              <th style={s.th}>Name</th>
              <th style={s.th}>Location</th>
              <th style={s.th}>Boxes</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ ...s.td, textAlign: 'center', padding: 40, color: '#8a8da4' }}>Loading...</td></tr>
            ) : devices.map((d) => (
              <tr key={d.id}>
                <td style={s.td}><span style={s.mono}>{d.device_number}</span></td>
                <td style={s.td}>{d.name || '—'}</td>
                <td style={s.td}>{d.location || '—'}</td>
                <td style={s.td}>{d.box_count}</td>
                <td style={s.td}>
                  {d.is_online ? (
                    <span style={s.badgeGreen}><Wifi size={12} /> Online</span>
                  ) : (
                    <span style={s.badgeRed}><WifiOff size={12} /> Offline</span>
                  )}
                </td>
                <td style={s.td}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={s.smBtn} onClick={() => navigate(`/lockers?device=${d.id}`)}>
                      <Grid3X3 size={12} /> Boxes
                    </button>
                    <button style={s.smBtn} onClick={() => syncBoxes(d.id)} disabled={syncing[d.id]}>
                      <RefreshCw size={12} /> {syncing[d.id] ? 'Syncing...' : 'Sync'}
                    </button>
                    <button style={s.smBtn} onClick={() => checkStatus(d.id)}>
                      <Wifi size={12} /> Ping
                    </button>
                    <button style={{...s.smBtn, background: 'rgba(77,144,254,0.1)', color: '#4d90fe'}} onClick={() => openEdit(d)}>
                      <Edit2 size={12} /> Edit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Device Modal */}
      {showAdd && (
        <div style={s.overlay} onClick={(e) => e.target === e.currentTarget && setShowAdd(false)}>
          <div style={s.modal}>
            <div style={s.modalHead}>
              <h3 style={{ fontSize: 16, fontWeight: 600 }}>Add Device</h3>
              <X size={18} style={{ cursor: 'pointer', color: '#8789a0' }} onClick={() => setShowAdd(false)} />
            </div>
            <div style={s.modalBody}>
              <label style={s.label}>Device Number</label>
              <input style={s.input} placeholder="e.g. 2100017335" value={form.device_number}
                onChange={(e) => setForm({ ...form, device_number: e.target.value })} />
              <label style={s.label}>Name</label>
              <input style={s.input} placeholder="e.g. Lobby Unit A" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <label style={s.label}>Location</label>
              <input style={s.input} placeholder="e.g. Tbilisi" value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </div>
            <div style={s.modalFoot}>
              <button style={s.outlineBtn} onClick={() => setShowAdd(false)}>Cancel</button>
              <button style={s.primaryBtn} onClick={addDevice}>Add Device</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div style={s.modalOverlay} onClick={() => setEditing(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Edit Device: {editing.device_number}</h3>
              <X size={20} style={{cursor:'pointer', color:'#8789a0'}} onClick={() => setEditing(null)} />
            </div>
            <div style={{padding: 20}}>
              <label style={s.label}>Name</label>
              <input style={s.input} placeholder="e.g. Lobby Unit A"
                value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} />
              
              <label style={s.label}>Location (short)</label>
              <input style={s.input} placeholder="e.g. Tbilisi Mall"
                value={editForm.location} onChange={e => setEditForm({...editForm, location: e.target.value})} />
              
              <label style={s.label}>Full Address</label>
              <input style={s.input} placeholder="e.g. 123 Rustaveli Ave, Tbilisi"
                value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} />
              
              <label style={s.label}>Support Phone</label>
              <input style={s.input} placeholder="e.g. +995 555 123456"
                value={editForm.support_phone} onChange={e => setEditForm({...editForm, support_phone: e.target.value})} />
              
              <div style={{display:'flex', gap: 10, marginTop: 20}}>
                <button style={s.outlineBtn} onClick={() => setEditing(null)}>Cancel</button>
                <button style={{...s.primaryBtn, flex: 1}} onClick={saveEdit} disabled={editSaving}>
                  {editSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700, color: '#e4e5ed' },
  panel: { background: '#0f1117', border: '1px solid #252733', borderRadius: 12, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '10px 16px', fontSize: 10, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: 0.8, color: '#8a8da4',
    background: '#0a0b0e', borderBottom: '1px solid #252733',
  },
  td: { padding: '12px 16px', fontSize: 13, borderBottom: '1px solid #1a1b25', color: '#e4e5ed' },
  mono: { fontFamily: "'Space Mono', monospace", fontSize: 12 },
  badgeGreen: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: 'rgba(139,198,62,0.1)', color: '#8BC63E' },
  badgeRed: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: 'rgba(255,68,102,0.1)', color: '#ff4466' },
  smBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px',
    background: 'transparent', border: '1px solid #252733', borderRadius: 6,
    color: '#8789a0', fontSize: 11, fontWeight: 500, cursor: 'pointer',
  },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px',
    background: '#8BC63E', border: 'none', borderRadius: 8,
    color: '#07080c', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  outlineBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
    background: 'transparent', border: '1px solid #252733', borderRadius: 8,
    color: '#8789a0', fontSize: 12, fontWeight: 500, cursor: 'pointer',
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
    zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#0f1117', border: '1px solid #252733', borderRadius: 12, width: 440, maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #252733' },
  modalTitle: { fontSize: 15, fontWeight: 600, color: '#e4e5ed' },
  modalHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1px solid #252733', color: '#e4e5ed' },
  modalBody: { padding: 20 },
  modalFoot: { padding: '14px 20px', borderTop: '1px solid #252733', display: 'flex', justifyContent: 'flex-end', gap: 8 },
  label: { display: 'block', fontSize: 11, fontWeight: 600, color: '#8789a0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, marginTop: 12 },
  input: {
    width: '100%', background: '#161821', border: '1px solid #252733', borderRadius: 8,
    padding: '10px 12px', fontSize: 13, color: '#e4e5ed', outline: 'none', boxSizing: 'border-box',
  },
};

